// Timeline slider with frame capture markers
import { useMemo } from 'react'
import { Slider } from '@/components/ui/slider'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface TimelineMarker {
  frameNumber: number
  isSelected?: boolean
  isFiltered?: boolean
  isHighlighted?: boolean
}

interface VideoTimelineProps {
  currentFrame: number
  totalFrames: number
  capturedFrames: Set<number>
  selectedFrames?: Set<number>
  filteredFrames?: Set<number>
  highlightedFrame?: number | null
  disabled?: boolean
  onFrameChange: (frame: number) => void
  onMarkerClick?: (frameNumber: number) => void
}

export function VideoTimeline({
  currentFrame,
  totalFrames,
  capturedFrames,
  selectedFrames = new Set(),
  filteredFrames,
  highlightedFrame,
  disabled = false,
  onFrameChange,
  onMarkerClick
}: VideoTimelineProps) {
  // Generate markers for captured frames
  const markers: TimelineMarker[] = useMemo(() => {
    return Array.from(capturedFrames).map(frameNumber => ({
      frameNumber,
      isSelected: selectedFrames.has(frameNumber),
      isFiltered: filteredFrames ? filteredFrames.has(frameNumber) : false,
      isHighlighted: highlightedFrame === frameNumber
    }))
  }, [capturedFrames, selectedFrames, filteredFrames, highlightedFrame])

  // Calculate marker position as percentage
  const getMarkerPosition = (frameNumber: number) => {
    if (totalFrames <= 0) return 0
    return (frameNumber / totalFrames) * 100
  }

  // Get marker color based on state
  const getMarkerColor = (marker: TimelineMarker) => {
    if (marker.isHighlighted) {
      return 'bg-yellow-400 ring-2 ring-yellow-300 shadow-lg scale-125'
    }
    if (marker.isSelected || marker.isFiltered) {
      return 'bg-blue-500 ring-1 ring-blue-300'
    }
    return 'bg-green-500'
  }

  const handleSliderChange = (value: number[]) => {
    onFrameChange(Math.floor(value[0]))
  }

  const handleMarkerClick = (e: React.MouseEvent, frameNumber: number) => {
    e.stopPropagation()
    if (onMarkerClick) {
      onMarkerClick(frameNumber)
    }
  }

  return (
    <div className="relative w-full py-4">
      {/* Frame markers layer - positioned above the slider */}
      <TooltipProvider>
        <div className="absolute inset-x-0 top-0 h-4 pointer-events-none z-10">
          {markers.map((marker) => (
            <Tooltip key={marker.frameNumber}>
              <TooltipTrigger asChild>
                <button
                  className={cn(
                    'absolute top-0 w-2.5 h-2.5 rounded-full transform -translate-x-1/2 cursor-pointer transition-all duration-150 pointer-events-auto hover:scale-150',
                    getMarkerColor(marker)
                  )}
                  style={{ left: `${getMarkerPosition(marker.frameNumber)}%` }}
                  onClick={(e) => handleMarkerClick(e, marker.frameNumber)}
                />
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="text-xs">
                  Frame {marker.frameNumber}
                  {marker.isSelected && ' • Selected'}
                </p>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </TooltipProvider>

      {/* Slider */}
      <div className="pt-4">
        <Slider
          value={[currentFrame]}
          min={0}
          max={totalFrames > 0 ? totalFrames - 1 : 0}
          step={1}
          onValueChange={handleSliderChange}
          disabled={disabled}
          className="w-full"
        />
      </div>

      {/* Legend */}
      {capturedFrames.size > 0 && (
        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span>Captured</span>
          </div>
          {selectedFrames.size > 0 && (
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span>Selected</span>
            </div>
          )}
          {highlightedFrame !== undefined && highlightedFrame !== null && (
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-yellow-400" />
              <span>Focused</span>
            </div>
          )}
          <span className="ml-auto">
            {capturedFrames.size} frame{capturedFrames.size !== 1 ? 's' : ''} captured
          </span>
        </div>
      )}
    </div>
  )
}
